'use strict';

import { ref } from 'vue';
import { defineStore } from 'pinia';
import { useFetch } from '../../app/composables/fetch';
import { camelCase, snakeCase } from '../../app/utils/change-case';

/**
 * Pinia store for a single user
 */
export const useUserStore = defineStore('user', () => {
  /**
   * Current page
   */
  const page = ref(1);

  /**
   * Current user
   */
  const user = ref(null);

  /**
   * All Users
   */
  const users = ref([]);

  /**
   * Loading
   */
  const loading = ref(false);

  /**
   * Submitting
   */
  const submitting = ref(false);

  /**
   * Error
   */
  const error = ref(null);

  /**
   * Get user by id
   * @param {Number} id - id of user
   * @returns {Void}
   */
  const getById = async (id) => {
    // Reset error
    error.value = null;

    // Call api
    loading.value = true;
    const { data, error: fetchError } = await useFetch(`users/${id}`)
      .get()
      .json();
    loading.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      return;
    }

    // Clear user
    user.value = null;

    // Change keys to camel case
    user.value = camelCase(data.value.data);
  };

  /**
   * Get user by id
   * @param {Number} id - id of user
   * @returns {Void}
   */
  const getByFirebaseId = async (firebaseId) => {
    // Reset error
    error.value = null;

    // Call api
    loading.value = true;
    const { data, error: fetchError } = await useFetch(
      `users/${firebaseId}?uid=true`,
    )
      .get()
      .json();
    loading.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      return;
    }

    // Clear user
    user.value = null;

    // Change keys to camel case
    user.value = camelCase(data.value.data);
  };

  /**
   * Get all users
   * @returns {Promise} users - array of user objects
   */
  const getAll = async () => {
    // Reset error
    error.value = null;

    // Call api
    loading.value = true;
    const { data, error: fetchError } = await useFetch('users').get().json();
    loading.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      return;
    }

    // Clear users
    users.value = [];

    // Change keys to camel case
    users.value = camelCase(data.value.data);
  };

  /**
   * Get more users
   * @param {Number} page - page to fetch
   * @returns {Promise} users - array of user objects
   */
  const getMore = async (page) => {
    // Reset error
    error.value = null;

    // Call api
    submitting.value = true;
    const { data, error: fetchError } = await useFetch(`users?page=${page}`)
      .get()
      .json();
    submitting.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      return;
    }

    // Change keys to camel case
    users.value = camelCase(data.value.data);
  };

  /**
   * Search users
   * @param {String} query - search query
   * @returns {Promise} users - array of user objects
   */
  const search = async (query) => {
    // Clear users
    users.value = [];

    // Reset error
    error.value = null;

    // Call api
    loading.value = true;
    const { data, error: fetchError } = await useFetch(`users?search=${query}`)
      .get()
      .json();
    loading.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      return;
    }

    // Change keys to camel case
    users.value = camelCase(data.value.data);
  };

  /**
   * Add user
   * @param {Obejct} user - user object
   * @returns {Promise} user - simple object of user
   */
  const add = async () => {
    // Reset error
    error.value = null;

    // Change keys to snake case
    const payload = snakeCase(user.value);

    // Call api
    submitting.value = true;
    const { data, error: fetchError } = await useFetch('users')
      .post(payload)
      .json();
    submitting.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      //throw error.value;
      return;
    }

    // Change keys to camel case
    user.value = camelCase(data.value.data);

    // Add user from response to list
    users.value.push(user.value);

    return Promise.resolve(user.value);
  };

  /**
   * Edit user
   * @param {Obejct} user - user object
   * @returns {Promise} user - simple object of user
   */
  const edit = async () => {
    // Reset error
    error.value = null;

    // Change keys to snake case
    const payload = snakeCase(user.value);

    // Call api
    submitting.value = true;
    const { data, error: fetchError } = await useFetch(`users/${user.value.id}`)
      .patch(payload)
      .json();
    submitting.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      //throw error.value;
      return;
    }

    // Change keys to camel case
    user.value = camelCase(data.value.data);

    return Promise.resolve(user.value);
  };

  /**
   * Delete user
   * @param {Number} userId - id of user
   * @returns {Promise} user - simple object of user
   */
  const _delete = async (userId) => {
    // Reset error
    error.value = null;

    // Call api
    submitting.value = true;
    const { error: fetchError } = await useFetch(`users/${userId}`).delete();
    submitting.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      //throw error.value;
      return;
    }

    // Remove user from list
    users.value = users.value.filter((user) => user.id !== userId);

    return Promise.resolve(true);
  };

  return {
    user,
    users,
    error,
    loading,
    submitting,
    getById,
    getByFirebaseId,
    getAll,
    getMore,
    search,
    add,
    edit,
    _delete,
  };
});
